//#region Imports

// Angular Imports
import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';

// CC-Common
import { DataGridFeaturesComponent, DxWidgetUtils, IconConstants, Utils } from 'cc-common';

// Devextreme imports
import { DxDataGridComponent, DxDropDownButtonComponent, DxFormComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// Third party imports
import { plainToClass } from 'class-transformer';

// Component imports
import { GridActionsMenuComponent } from '../../../shared-module/grid-actions-menu/grid-actions-menu.component';

// Constant imports
import { ActionConstants } from '../../../_constants/action-constants';
import { Constants } from '../../../_constants/constants';
import { MessageInterval } from '../../../_constants/messages-intervals';
import { StringFormatConstants } from '../../../_constants/string-format-constants';
import { AccConstants } from '../../_constants/acc-constant';

// Service imports
import { HelperService } from '../../../_services/shared-services/helper.service';
import { PrecisionService } from '../../../_services/shared-services/precision.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { SharedAccApplicationService } from '../../_services/shared-acc-application.service';

// Model imports
import { EmailReferenceParameterDto } from '../../../_models/email-reference-parameter-dto';
import { KeyValueResultDto } from '../../../_models/key-value-result-dto';
import { EmailAttachmentRawDto } from '../../../ciramail-module/_models/email-composer/email-attachment-raw-dto';
import { OwnerSearchResultDto } from '../../../search/_models/owner-search-result-dto';
import { ActionMenuEvent, ActionMenuInfo } from '../../../shared-module/_models/action-menu-info';
import { AccAddParameterDto } from '../../_models/acc-add-parameter-dto';
import { AccApplicationParameterDto } from '../../_models/acc-application-parameters-dto';
import { AccAttachmentRawDto } from '../../_models/acc-attachment-raw-dto';
import { AccBaseParameterDto } from '../../_models/acc-base-parameter-dto';
import { ACCEditorParameterDto } from '../../_models/acc-editor-parameter-dto';
import { AccEditorResultDto } from '../../_models/acc-editor-result-dto';
import { AccHistoryResultDto } from '../../_models/acc-history-result-dto';
import { AccItemRawDto } from '../../_models/acc-item-raw-dto';
import { AccOpenStatusResultDto } from '../../_models/acc-open-status-result-dto';
import { AccProjectRawDto } from '../../_models/acc-project-raw-dto';
import { AccUpdateParameterDto } from '../../_models/acc-update-parameter-dto';
import { AccUsersForAssignmentResultDto } from '../../_models/acc-users-for-assignment-result-dto';
import { AccItemFinalizeStatusParameterDto } from '../../_models/acc-item-finalize-status-parameter-dto';
import { AddACCResultDto } from '../../_models/add-acc-result-dto';

//#endregion

@Component({
    selector: 'app-architectural-application-editor',
    templateUrl: './architectural-application-editor.component.html',
    styleUrls: ['./architectural-application-editor.component.scss']
})
export class ArchitecturalApplicationEditorComponent implements OnInit {
    //#region Component variables

    @ViewChildren('gridActions') gridActions: QueryList<GridActionsMenuComponent<any>>;
    @ViewChild('accEditorForm', { static: false }) accEditorForm: DxFormComponent;
    @ViewChild('lineItemGrid', { static: false }) lineItemGrid: DxDataGridComponent;
    @ViewChild('lineItemGridFeatures', { static: false }) lineItemGridFeatures: DataGridFeaturesComponent;
    @ViewChildren('accItemActionButton') accItemActionButtons: QueryList<DxDropDownButtonComponent>;

    @Input() visible = false;
    @Input() popupTitle: string;
    @Input() accType = 'open-application';
    @Input() customerId: string;
    @Input() communityId: number;
    @Input() accItemId: number;
    @Input() accId: number;
    @Input() emailCacheId: number;
    @Input() linkedEmailId: number;
    @Input() emailAccount: string;
    @Input() emailFolder: string;
    @Input() emailAttachments: Array<EmailAttachmentRawDto> = [];

    @Output() closeApplicationEditorPopup = new EventEmitter<boolean>();

    loadingVisible = false;
    defaultLabelLocation = Constants.defaultFormLabelLocation;
    searchCustomerButton: any;
    searchText = '';
    remainingWords = 100;

    isInternalUser = false;

    iconClose = IconConstants.CLOSE;
    iconSave = IconConstants.SAVE;
    iconCheck = IconConstants.CHECK;
    iconLog: string = IconConstants.HISTORY;
    iconAttach: string = IconConstants.ATTACHMENT;

    today = new Date();

    assignedUser: any;
    assignToColumns: any[];

    showLineItemFormEditor = false;
    showChangeOwnerEditor = false;
    showBoardApprovalPopup = false;
    showTransactionLog = false;

    transactionLogTitle: string;
    selectedProjectAttachments: any[] = [];
    selectedItemAttachments: any[] = [];

    showNotesEditor = false;
    hasNotes = false;
    accNoteItemId: number;

    showFinalizedItem = false;

    apiProjectData: AccProjectRawDto = new AccProjectRawDto();
    apiEditorData: AccEditorResultDto = new AccEditorResultDto();
    projectData: AccProjectRawDto = new AccProjectRawDto();
    editorData: AccEditorResultDto = new AccEditorResultDto();
    lineItemData: AccItemRawDto = new AccItemRawDto();
    accEditorItems: AccItemRawDto[] = [];
    accEditorDeleteItems: AccItemRawDto[] = [];
    tempAccEditorItems: AccItemRawDto[] = [];
    transactionLogResultData: AccHistoryResultDto[] = [];
    isPriorOwner = false;
    dateFormat = StringFormatConstants.dateFormat;

    notesEditorParam: AccBaseParameterDto = new AccBaseParameterDto();
    notesType: string;

    itemNotes = AccConstants.ITEM_NOTES;
    projectNotes = AccConstants.PROJECT_NOTES;
    lineItemLog = AccConstants.ITEM_LOGS;
    assignTogridBoxValue: any[];
    isUpdateCustomer = false;

    showAttachmentEditor = false;
    lineItemAttachment: AccAttachmentRawDto[] = [];

    lineItemId: number;
    isProjectDocs = false;
    isItemDocs = false;
    projectAccItemId: number;
    projectAttachmentCount: number;
    tempProjectAttachments: AccAttachmentRawDto[] = [];
    projectAttachmentParam: AccAttachmentRawDto[] = [];
    lineItemParam: AccItemRawDto[] = [];

    currencyFormat: string;
    isSaved = false;
    actionMenuItems: Array<ActionMenuInfo> = [];
    allowLimitedAccess = false;

    //#endregion

    //#region Component life cycle methods

    constructor(
        private readonly errorService: ServerErrorService,
        private readonly accApplicationService: SharedAccApplicationService,
        private readonly helperService: HelperService,
        private readonly precisionService: PrecisionService
    ) {
        this.onSearchClick = this.onSearchClick.bind(this);
        this.isActionVisible = this.isActionVisible.bind(this);
    }

    ngOnInit(): void {
        this.currencyFormat = this.precisionService.getCurrencyFormat();

        this.getAssignToColumns();
        this.prepareActionItems();

        if (Utils.notNullAndDefined(this.communityId) && Utils.notNullAndDefined(this.customerId)) {
            if (Utils.notNullAndDefined(this.linkedEmailId)) {
                this.loadOpenACCItems();
            } else {
                this.populateOpenAccEditor(null);
            }
        }
    }

    //#endregion

    onHidden(): void {
        this.visible = false;
        this.closeApplicationEditorPopup.emit(false);
    }

    onClosePopup(): void {
        if (Utils.isFalse(this.isSaved)) {
            const isEditorEdited = Utils.notEqualsIgnoreCase(
                JSON.stringify(this.apiEditorData),
                JSON.stringify(this.editorData)
            );
            const isProjectEdited = Utils.notEqualsIgnoreCase(
                JSON.stringify(this.apiProjectData),
                JSON.stringify(this.projectData)
            );

            if (Utils.isTrue(isEditorEdited) || Utils.isTrue(isProjectEdited)) {
                const dialog = this.helperService.getConfirmationDialog(
                    'Confirmation',
                    'If you close the form, you will lose all your <br />changes. Are you sure you want to continue?',
                    'Yes',
                    'No',
                    'success'
                );

                dialog.show().then((answer: boolean) => {
                    if (Utils.isTrue(answer)) {
                        this.onHidden();
                    }
                });
            } else {
                this.onHidden();
            }
        } else {
            this.onHidden();
        }
    }

    onSearchClick(): void {
        if (this.searchText.length < 3) {
            this.errorService.updateSubjectErrorMessage('Please provide at least 3 characters to search');
        } else {
            this.showChangeOwnerEditor = true;
        }
    }

    onBoardApprovalClick(): void {
        this.showBoardApprovalPopup = true;
    }

    onProjectNotesClick(): void {
        this.notesEditorParam.communityId = this.communityId;
        this.notesEditorParam.customerId = this.customerId;
        this.notesEditorParam.accItemId = this.accItemId;
        this.notesEditorParam.clearCache = true;
        this.notesType = this.projectNotes;

        this.showNotesEditor = true;
    }

    onItemNotesClick(data: AccItemRawDto): void {
        this.notesEditorParam.communityId = this.communityId;
        this.notesEditorParam.customerId = this.customerId;
        this.notesEditorParam.accItemId = data.accItemId;
        this.notesEditorParam.clearCache = true;
        this.notesType = this.itemNotes;

        this.accNoteItemId = data.accItemId;
        this.showNotesEditor = true;
    }

    onNoteSave(refresh: boolean): void {
        this.showNotesEditor = false;

        /**
         * Instead of refreshing the grid, just set the hasNotes flag of item, to update the icon
         * Find the item
         */

        const accNoteItem = this.accEditorItems.find((item) => Utils.equals(item.accItemId, this.accNoteItemId));

        if (Utils.notNullAndDefined(accNoteItem)) {
            accNoteItem.hasNotes = Utils.isTrue(refresh) ? true : accNoteItem.hasNotes;
        }

        this.accNoteItemId = null;
    }

    onProjectDocAttachmentClick(): void {
        this.projectAccItemId = this.accItemId;
        this.lineItemAttachment = [];
        this.projectData.items.forEach((a) => {
            a.itemAttachments.forEach((item) => {
                item.accItemId = a.accItemId;
                this.lineItemAttachment.push(item);
            });
        });
        this.isProjectDocs = true;
        this.isItemDocs = false;
        this.showAttachmentEditor = true;
    }

    onLineItemAttachmentClick(data: AccItemRawDto): void {
        this.projectAccItemId = this.accItemId;
        this.lineItemAttachment = [];
        if (Utils.notNullAndDefined(data)) {
            this.lineItemId = Utils.notNullAndDefined(data.accItemId) ? data.accItemId : data.id;
            data.itemAttachments.forEach((attachment) => {
                attachment.accItemId = this.lineItemId;
            });
            this.lineItemAttachment = data.itemAttachments;
        }
        this.isProjectDocs = false;
        this.isItemDocs = true;
        this.showAttachmentEditor = true;
    }

    onAttachmentUpdate(data: any): void {
        if (Utils.notNullAndDefined(data)) {
            if (Utils.isTrue(this.isProjectDocs)) {
                this.projectData.projectAttachments = data;
                this.projectAttachmentCount = this.projectData.projectAttachments.length;
            } else {
                this.projectData.projectAttachments = data.projectDocs;
                this.accEditorItems.forEach((item) => {
                    const id = Utils.notNullAndDefined(item.accItemId) ? item.accItemId : item.id;
                    if (Utils.equals(id, this.lineItemId)) {
                        item.itemAttachments = data.itemDocs;
                    }
                });
            }
        }
        this.lineItemId = null;
    }

    onAttachmentClose(forceRefresh: boolean): void {
        this.showAttachmentEditor = false;

        if (Utils.isFalse(forceRefresh)) {
            this.projectData.projectAttachments = this.projectData.projectAttachments.filter((d) =>
                Utils.isNullOrEmpty(d.action)
            );
            this.lineItemAttachment = this.lineItemAttachment.filter((d) => Utils.isNullOrEmpty(d.action));
        }
    }

    onTransactionLogClick(data: any, logType: string): void {
        const param: AccBaseParameterDto = new AccBaseParameterDto();
        param.communityId = this.communityId;
        param.customerId = this.customerId;
        param.accItemId = this.accItemId;
        param.clearCache = true;
        let transactionLogService: any;
        if (Utils.equals(logType, this.lineItemLog)) {
            this.transactionLogTitle = `Log of ${data.accItemId}`;
            transactionLogService = this.accApplicationService.getAccItemHistoryLog(param);
        } else {
            this.transactionLogTitle = `Architectural Request Approval Log: ${this.accId}`;
            transactionLogService = this.accApplicationService.getAccProjectHistoryLog(param);
        }
        this.loadingVisible = true;
        transactionLogService.subscribe(
            (result) => {
                this.transactionLogResultData = result;
                this.showTransactionLog = true;
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    onTextAreaKeyUp(e: any): void {
        if (Utils.notNullAndDefined(e.event) && e.event.target.textLength > 0) {
            setTimeout(() => {
                this.remainingWords = 100 - e.event.target.textLength;
            }, 50);
        }
    }

    onOwnerSelection(data: OwnerSearchResultDto): void {
        if (Utils.notNullAndDefined(data)) {
            this.projectData.communityId = data.communityId;
            this.projectData.customerId = data.accountNumber;
            this.searchText = data.propertyAddress;
            this.communityId = data.communityId;
            this.customerId = data.accountNumber;
            this.isPriorOwner = data.currentOwner;

            setTimeout(() => {
                this.populateOpenAccEditor(data.combinedOwner);
            }, 200);
        }

        this.showChangeOwnerEditor = false;
    }

    onChangeOwnerEditorClose(): void {
        this.showChangeOwnerEditor = false;
    }

    onAssignedToSelection(event: AccUsersForAssignmentResultDto[]): void {
        if (Utils.notNullAndDefined(event)) {
            this.projectData.assignedTo = event[0].userId;
        }
    }

    onStatusClick(item: KeyValueResultDto, row: AccItemRawDto): void {
        row.statusId = item.key;
    }

    onAddNewLineItem(): void {
        this.lineItemData = new AccItemRawDto();

        // Set crud action to ADD
        this.lineItemData.crudAction = ActionConstants.ADD;

        this.showLineItemFormEditor = true;
    }

    onRemoveClick(data: AccItemRawDto): void {
        // Set the CRUD action to DELETE and add in accEditorDeleteItems list
        data.crudAction = ActionConstants.DELETE;
        this.accEditorDeleteItems.push(data);

        this.accEditorItems = this.accEditorItems.filter((row) => Utils.notEquals(row.id, data.id));
    }

    onLineItemAdd(data: AccItemRawDto): void {
        if (Utils.notNullAndDefined(data) && Utils.isNullOrUndefined(data.id)) {
            data.id = this.accEditorItems.length + 1;
            this.accEditorItems.push(data);
        }

        DxWidgetUtils.deferredRepaint(this.lineItemGrid, 500);
        this.showLineItemFormEditor = false;
    }

    onLineItemEditorClose(): void {
        this.showLineItemFormEditor = false;
        this.lineItemData = null;
    }

    onEditLineItemClick(data: AccItemRawDto, rowIndex: number): void {
        this.lineItemId = data.id;
        this.lineItemData = data;
        this.showLineItemFormEditor = true;
    }

    onShowFinalizeItemsClick(): void {
        this.toggleFinalizeItems();
    }

    onSaveClick(): void {
        this.setLineItemAttachment();

        if (Utils.notNullAndDefined(this.accEditorForm) && Utils.notNullAndDefined(this.accEditorForm.instance)) {
            const validator = this.accEditorForm.instance.validate();

            if (Utils.isTrue(validator.isValid)) {
                if (Utils.isNullOrEmpty(this.projectData.description)) {
                    this.errorService.updateSubjectErrorMessage('Please provide Project description');
                    return;
                } else if (this.projectData.description.length < 10) {
                    this.errorService.updateSubjectErrorMessage(
                        'Please provide 10-100 characters in Project description'
                    );
                    return;
                }

                if (Utils.isNullOrEmpty(this.lineItemParam)) {
                    this.errorService.updateSubjectErrorMessage('Please add atleast one line item');
                    return;
                }

                if (Utils.notNullAndDefined(this.accItemId)) {
                    this.setProjectAttachment();
                    this.editAcc();
                } else {
                    this.addAcc();
                }
            }
        }
    }

    setProjectAttachment(): void {
        if (Utils.notNullOrEmpty(this.tempProjectAttachments.length)) {
            const projectDocs = [...this.projectData.projectAttachments, ...this.tempProjectAttachments].filter(
                (
                    (s) => (a) =>
                        !s.has(a.fileName) && s.add(a.fileName)
                )(new Set())
            );
            this.projectAttachmentParam = projectDocs.filter((doc) => Utils.notNullAndDefined(doc.action));
        } else {
            this.projectAttachmentParam = this.projectData.projectAttachments;
        }
    }

    setLineItemAttachment(): void {
        const lineItemAttachment = this.lineItemAttachment;
        const lineItemParam = [];

        this.accEditorItems.forEach((item) => {
            let itemObj: AccItemRawDto = new AccItemRawDto();
            itemObj = Object.assign(itemObj, item);

            const id = Utils.notNullAndDefined(item.accItemId) ? item.accItemId : item.id;
            if (Utils.equals(id, this.lineItemId)) {
                const itemDocs = [...itemObj.itemAttachments, ...lineItemAttachment].filter(
                    (
                        (s) => (a) =>
                            !s.has(a.fileName) && s.add(a.fileName)
                    )(new Set())
                );

                itemObj.itemAttachments = itemDocs.filter((doc) => Utils.notNullAndDefined(doc.action));
            }
            lineItemParam.push(itemObj);
        });

        this.lineItemParam = lineItemParam;
    }

    onOwnerClick(): void {
        this.helperService.openDefaultPropertyPageInNewWindow(
            this.projectData.communityId,
            this.projectData.customerId
        );
    }

    isActionVisible(item: ActionMenuInfo, data: AccItemRawDto): boolean {
        /// Actionable at Item level - " "Edit", "Remove", "Finalize/UnFinalize", "UpdateStatus"
        /// Note for setting ActionNameForFinalizeItem :
        ///        -If the accItem is finalized, show "UnFinalize Item" as the actionName
        ///        -If the queue is open and item is not finalized and the status is in one of the below, show "Finalize Item" as the actionName
        ///         Status - Cancelled(-1),  Approved(10), ApprovedWithStipulation(11), Denied(12)
        ///        - Above conditions doesnt satisfy, then no action for Finalize/Unfinalize
        /// Note for allowing "UpdateStatus" - Dont allow update status if the item is finalized.
        /// (Note in asp.net - even if the action is finalized, we can change the status[ we assume it is a bug])
        let isActionItemVisible = false;
        if (
            Utils.equalsIgnoreCase(item.action, AccConstants.EDIT) ||
            Utils.equalsIgnoreCase(item.action, AccConstants.REMOVE) ||
            Utils.equalsIgnoreCase(item.action, AccConstants.CHANGE_STATUS) ||
            (Utils.equalsIgnoreCase(item.action, AccConstants.UNFINALIZE) && Utils.isTrue(data.finalized)) ||
            (Utils.equalsIgnoreCase(item.action, AccConstants.FINALIZE) &&
                Utils.isFalse(data.finalized) &&
                Utils.equalsIgnoreCase(this.accType, 'open-application') &&
                (Utils.equals(data.statusId, -1) ||
                    Utils.equals(data.statusId, 10) ||
                    Utils.equals(data.statusId, 11) ||
                    Utils.equals(data.statusId, 12)))
        ) {
            isActionItemVisible = true;
        }
        return isActionItemVisible;
    }

    onActionTemplateClick(record: AccItemRawDto, rowIndex: number): void {
        this.prepareChangeStatus(record, rowIndex);
    }

    onActionItemClick(event: ActionMenuEvent<any>): void {
        const actionItem = event.item.action;
        const parentActionItem = event.item.parent;

        if (Utils.equals(actionItem, AccConstants.EDIT)) {
            this.onEditLineItemClick(event.record, event.rowIndex);
        } else if (Utils.equals(actionItem, AccConstants.REMOVE)) {
            this.onRemoveClick(event.record);
        } else if (Utils.equals(parentActionItem, AccConstants.CHANGE_STATUS)) {
            this.onStatusClick(event.item.info, event.record);
        } else if (
            Utils.equals(actionItem, AccConstants.FINALIZE) ||
            Utils.equals(actionItem, AccConstants.UNFINALIZE)
        ) {
            this.onFinalizeUnFinalizeClick(event.record);
        }
    }

    private toggleFinalizeItems(): void {
        if (Utils.isTrue(this.showFinalizedItem)) {
            this.accEditorItems = this.tempAccEditorItems;
        } else {
            this.accEditorItems = this.tempAccEditorItems.filter((a) => Utils.isFalse(a.finalized));
        }
    }

    private onFinalizeUnFinalizeClick(record: AccItemRawDto): void {
        if (Utils.notNullAndDefined(record)) {
            const param: AccItemFinalizeStatusParameterDto = new AccItemFinalizeStatusParameterDto();
            param.accItemId = record.accItemId;
            param.communityId = this.communityId;
            param.customerId = this.customerId;
            param.finalized = Utils.isFalse(record.finalized);
            param.clearCache = true;
            const message = Utils.isTrue(param.finalized) ? 'Finalized' : 'UnFinalized';
            this.loadingVisible = true;
            this.accApplicationService.accFinalizeUnfinalizeItem(param).subscribe(
                () => {
                    this.loadingVisible = false;
                    notify(
                        `Architectural Application item ${param.accItemId} ${message} successfully`,
                        'success',
                        MessageInterval.successMsgInterval
                    );
                    this.populateOpenAccEditor(null);
                },
                (error) => {
                    this.loadingVisible = false;
                    this.errorService.displayError(error);
                }
            );
        }
    }

    private prepareChangeStatus(record: AccItemRawDto, rowIndex: number): void {
        const statusMenu: Array<ActionMenuInfo> = this.editorData.statusResult.map((status) => {
            return plainToClass(ActionMenuInfo, {
                icon: null,
                action: status.key,
                name: status.displayText,
                parent: AccConstants.CHANGE_STATUS,
                info: status,
                disabled: Utils.equals(status.key, Number(record.statusId)) ? true : false,
                items: []
            });
        });
        this.actionMenuItems.forEach((menu) => {
            if (Utils.equalsIgnoreCase(menu.action, AccConstants.CHANGE_STATUS)) {
                menu.items = statusMenu;
            }
        });

        if (Utils.notNullAndDefined(this.gridActions)) {
            const gridAction = this.gridActions.find((ga) => Utils.equals(ga.rowIndex, rowIndex));
            gridAction.repaint();
        }
    }

    private loadOpenACCItems(): void {
        const baseParams: AccApplicationParameterDto = new AccApplicationParameterDto();
        baseParams.communityId = this.communityId;
        baseParams.customerId = this.customerId;
        baseParams.filterOption = 2; // As we are loading data for All Application
        baseParams.clearCache = true;

        this.loadingVisible = true;
        this.accApplicationService.getAccOpenApplication(baseParams).subscribe(
            (result: AccOpenStatusResultDto) => {
                if (result.openItems.length > 0) {
                    const dialog = this.helperService.getConfirmationDialog(
                        'Confirmation',
                        'There were some Open Architectural Applications created for this Customer. Are you sure you want to continue ?'
                    );
                    dialog.show().then((answer: boolean) => {
                        if (Utils.isTrue(answer)) {
                            this.visible = true;
                            this.emailCacheId = this.linkedEmailId;
                            this.populateOpenAccEditor(null);
                        } else {
                            this.onClosePopup();
                        }
                    });
                } else {
                    this.visible = true;
                    this.emailCacheId = this.linkedEmailId;
                    this.populateOpenAccEditor(null);
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private addAcc(): void {
        // Build parameter dto
        const param: AccAddParameterDto = new AccAddParameterDto();
        param.projectData = this.projectData;
        param.projectData.items = this.lineItemParam;

        if (
            Utils.notNullAndDefined(this.emailCacheId) &&
            Utils.notNullOrEmpty(this.emailAccount) &&
            Utils.notNullOrEmpty(this.emailFolder)
        ) {
            const emailReferenceParam = new EmailReferenceParameterDto();
            emailReferenceParam.emailCacheId = this.emailCacheId;
            emailReferenceParam.emailAccount = this.emailAccount;
            emailReferenceParam.folder = this.emailFolder;

            param.emailReference = emailReferenceParam;
        }

        // Show loading
        this.loadingVisible = true;

        this.accApplicationService.addAcc(param).subscribe(
            (result: AddACCResultDto) => {
                const successMessage = `Architectural Project #${result.accId} is added successfully`;
                const dialog = this.helperService.getOkDialog('Status', successMessage);
                dialog.show().then(() => {
                    this.isSaved = true;
                    this.closeApplicationEditorPopup.emit(true);
                });
                // Hide loading
                this.loadingVisible = false;
            },
            (error) => {
                // Hide loading
                this.loadingVisible = false;

                // Display error
                this.errorService.displayError(error);
            }
        );
    }

    private editAcc(): void {
        // Build parameter dto
        const param: AccUpdateParameterDto = new AccUpdateParameterDto();
        param.accItemId = this.accItemId;
        param.projectData = this.projectData;

        param.projectData.items = [
            ...this.lineItemParam,
            ...this.accEditorDeleteItems.filter((item) => Utils.notNullAndDefined(item.accItemId)) // Ignore new line item if deleted
        ];

        param.projectData.projectAttachments = this.projectAttachmentParam;
        param.updateCustomer = this.isUpdateCustomer;

        // Show loading
        this.loadingVisible = true;

        this.accApplicationService.updateAcc(param).subscribe(
            () => {
                notify('Architectural Application updated successfully', 'success', MessageInterval.successMsgInterval);
                this.isSaved = true;
                this.closeApplicationEditorPopup.emit(true);

                // Hide loading
                this.loadingVisible = false;
            },
            (error) => {
                this.projectData.projectAttachments = this.tempProjectAttachments;

                // Hide loading
                this.loadingVisible = false;

                // Display error
                this.errorService.displayError(error);
            }
        );
    }

    private populateOpenAccEditor(owner: string): void {
        const accEditorParam: ACCEditorParameterDto = new ACCEditorParameterDto();
        accEditorParam.communityId = this.communityId;
        accEditorParam.customerId = this.customerId;
        accEditorParam.accId = Utils.notNullAndDefined(this.accId) ? this.accId : null;
        accEditorParam.accItemId = Utils.notNullAndDefined(this.accItemId) ? this.accItemId : null;

        this.projectData.projectAttachments = [];
        this.loadingVisible = true;
        this.accApplicationService.getAccOpenEditor(accEditorParam).subscribe(
            (result: AccEditorResultDto) => {
                this.apiEditorData = Object.assign(this.apiEditorData, result);
                this.allowLimitedAccess = this.apiEditorData.allowLimitedEditAccess;
                this.apiProjectData = Object.assign(this.apiProjectData, result.projectData);

                this.editorData = result;
                this.projectData = result.projectData;

                this.tempProjectAttachments = result.projectData.projectAttachments;
                this.projectAttachmentCount = this.projectData.projectAttachments.length;
                this.tempAccEditorItems = result.projectData.items;
                this.searchText = this.editorData.property;

                this.assignTogridBoxValue = [this.projectData.assignedTo];
                if (Utils.isNullOrUndefined(this.editorData.owner)) {
                    this.editorData.owner = owner;
                }
                if (Utils.isFalse(this.editorData.isCurrentOwner)) {
                    this.updateOwner();
                }
                if (Utils.notNullAndDefined(this.projectData.description)) {
                    this.remainingWords = 100 - this.projectData.description.length;
                }

                this.accEditorItems = this.projectData.items;
                this.accEditorItems.forEach((item, itemIndex) => {
                    item.id = itemIndex + 1;

                    // Set the CRUD action to EDIT if accItemId is not null

                    if (Utils.notNullAndDefined(item.accItemId)) {
                        item.crudAction = ActionConstants.EDIT;
                    }
                });
                this.toggleFinalizeItems();

                this.projectData.receivedDate = Utils.notNullAndDefined(this.projectData.receivedDate)
                    ? this.projectData.receivedDate
                    : this.today;

                this.editorData.lastModifiedDate = Utils.notNullAndDefined(this.editorData.lastModifiedDate)
                    ? this.editorData.lastModifiedDate
                    : this.today;
                this.loadingVisible = false;

                DxWidgetUtils.deferredRepaint(this.lineItemGrid, 500);

                this.lineItemGridFeatures.onToolbarAction('ResetLayout');
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private updateOwner(): void {
        const dialog = this.helperService.getConfirmationDialog(
            'Warning: Outdated Owner',
            'The selected owner for this ACC is no longer the current owner. <br/> Please update ACC information',
            'Update',
            'Cancel',
            'success',
            'default'
        );
        dialog.show().then((answer) => {
            if (Utils.isTrue(answer)) {
                this.isUpdateCustomer = true;
            }
        });
    }

    private getAssignToColumns(): void {
        this.assignToColumns = [
            {
                dataField: 'name',
                dataType: 'string',
                caption: 'Name'
            },
            {
                dataField: 'userId',
                dataType: 'string',
                caption: 'User Id'
            },
            {
                dataField: 'type',
                dataType: 'string',
                caption: 'Type'
            },
            {
                dataField: 'role',
                dataType: 'string',
                caption: 'Role'
            },
            {
                dataField: 'primary',
                dataType: 'boolean',
                caption: 'Primary',
                cellTemplate: 'booleanValueCellTemplate'
            },
            {
                dataField: 'backup',
                dataType: 'boolean',
                caption: 'Backup',
                cellTemplate: 'booleanValueCellTemplate'
            }
        ];
    }

    private prepareActionItems(): void {
        this.actionMenuItems = [
            {
                icon: null,
                action: AccConstants.EDIT,
                name: 'Edit',
                disabled: false,
                parent: null,
                items: []
            },
            {
                icon: null,
                action: AccConstants.REMOVE,
                name: 'Remove',
                disabled: false,
                parent: null,
                items: []
            },
            {
                icon: null,
                action: AccConstants.FINALIZE,
                name: 'Finalize Item',
                disabled: false,
                parent: null,
                items: []
            },
            {
                icon: null,
                action: AccConstants.UNFINALIZE,
                name: 'UnFinalize Item',
                disabled: false,
                parent: null,
                items: []
            },
            {
                icon: null,
                action: AccConstants.CHANGE_STATUS,
                name: 'Update Status',
                disabled: false,
                parent: null,
                items: []
            }
        ];
    }
}
